import { AnimatePresence, motion, type Transition } from 'framer-motion';
import { memo, useCallback, useRef, useState } from 'react';
import { classNames } from '~/utils/classNames';
import { FILE_SIZE_LIMIT_MB_PERSONAL_TIER, MAX_FILE_UPLOADS } from '~/utils/constants';
import { cubicEasingFn } from '~/utils/easings';
import { renderLogger } from '~/utils/logger';

const transition = { duration: 0.15, ease: cubicEasingFn } satisfies Transition;

interface PromptDropZoneProps {
  show?: boolean;
  onDrop?: (event: React.DragEvent<HTMLDivElement>) => void;
  disabled?: boolean;
}

export const PromptDropZone = memo(({ show, onDrop, disabled }: PromptDropZoneProps) => {
  renderLogger.trace('PromptDropZone');

  const [isOverDropZone, setIsOverDropZone] = useState(false);

  const dropZoneRef = useRef<HTMLDivElement>(null);

  const handleDropZoneDragEnter = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsOverDropZone(true);
  }, []);

  const handleDropZoneDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();

    if (!dropZoneRef.current?.contains(event.relatedTarget as Node)) {
      setIsOverDropZone(false);
    }
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsOverDropZone(false);
      onDrop?.(event);
    },
    [onDrop],
  );

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="absolute rounded-2xl bg-bolt-elements-prompt-background inset-px z-2 p-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={transition}
        >
          <div
            ref={dropZoneRef}
            className={classNames(
              'flex flex-col items-center justify-center gap-1.5 w-full h-full border border-dashed rounded-lg p-4',
              {
                'border-bolt-elements-borderColor bg-bolt-elements-prompt-background text-bolt-elements-textTertiary':
                  !isOverDropZone,
                'border-bolt-elements-borderColorActive bg-alpha-accent-10 text-accent-500': isOverDropZone,
              },
            )}
            onDragOver={handleDropZoneDragEnter}
            onDragLeave={handleDropZoneDragLeave}
            onDrop={disabled ? undefined : handleDrop}
          >
            <div className="flex items-center gap-3">
              <span className="i-ph:image text-xl -rotate-10 opacity-80" />
              <span
                className={classNames({
                  'text-bolt-elements-textSecondary': !isOverDropZone,
                  'text-accent-500': isOverDropZone,
                })}
              >
                Drop files here to add to chat
              </span>
              <span className="i-ph:file-duotone text-xl rotate-10 opacity-80" />
            </div>
            <span className="text-xs opacity-80">
              A maximum of {MAX_FILE_UPLOADS} uploads per message at {FILE_SIZE_LIMIT_MB_PERSONAL_TIER} MB each
            </span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});
